import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, ArrowClockwise, SealCheck, Hourglass, ChatCircleText, WarningCircle } from '@phosphor-icons/react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { errText } from '../../../utils/errors';
import VolunteersPanel from '../../../pages/VolunteersPanel';
import { MatrixCell, CellSwatch } from './MatrixCell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pct = (m, c) => (c ? `${Math.round((m / c) * 100)}%` : '—');

const SummaryCard = ({ icon: Icon, label, value, sub, testId, tone }) => (
  <div className="rounded-sm border border-stone-200 bg-white px-4 py-3" data-testid={testId}>
    <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${tone}`}><Icon size={14} />{label}</div>
    <p className="mt-1 text-2xl font-heading font-semibold text-stone-900">{value}</p>
    {sub && <p className="text-xs text-stone-500">{sub}</p>}
  </div>
);

const FILTERS = [
  { key: 'pending', es: 'Pendientes de monitorizar', en: 'Pending monitoring' },
  { key: 'queries', es: 'Con queries abiertas', en: 'With open queries' },
  { key: 'todo', es: 'Con cuestionarios sin hacer', en: 'With questionnaires to do' },
];

const cellMatches = (cell, f) => {
  if (!cell || cell.state === 'na') return false;
  const items = cell.items || [];
  if (f === 'pending') return items.some(i => i.status === 'completed' && !i.monitored);
  if (f === 'queries') return (cell.queries || 0) > 0;
  return items.some(i => i.status === 'not_started');
};

const Legend = ({ lang }) => {
  const es = lang === 'es';
  const items = [
    ['not_started', es ? 'Falta alguno por empezar' : 'Something not started'],
    ['in_progress', es ? 'Guardado sin terminar' : 'Saved, not finished'],
    ['completed', es ? 'Todos terminados' : 'All finished'],
    ['monitored', es ? 'Terminados y monitorizados' : 'Finished and monitored'],
    ['na', es ? 'No aplica' : 'Not applicable'],
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-stone-600" data-testid="mx-legend">
      {items.map(([st, label]) => (
        <span key={st} className="inline-flex items-center gap-1.5"><CellSwatch state={st} queries={0} size="h-4 w-4" />{label}</span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="h-3.5 min-w-[14px] rounded-full bg-red-600 text-center text-[9px] font-bold leading-[14px] text-white">2</span>
        {es ? 'Queries abiertas' : 'Open queries'}
      </span>
    </div>
  );
};

export default function MonitoringMatrixPanel({ studyId, lang = 'es' }) {
  const es = lang === 'es';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [includeGrey, setIncludeGrey] = useState(false);
  const [search, setSearch] = useState('');
  const [hover, setHover] = useState({ row: null, col: null });
  const [openTarget, setOpenTarget] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/studies/${studyId}/monitoring-matrix`, { withCredentials: true })
      .then(r => setData(r.data)).catch(e => toast.error(errText(e))).finally(() => setLoading(false));
  }, [studyId]);
  useEffect(() => { load(); }, [load]);

  const activeFilters = FILTERS.filter(f => filters[f.key]).map(f => f.key);
  const rows = useMemo(() => (data?.rows || [])
    .filter(r => includeGrey || !r.grey)
    .filter(r => !search.trim() || (r.code || '').toLowerCase().includes(search.trim().toLowerCase())), [data, includeGrey, search]);

  const openQuestionnaire = (row, visit, item) => setOpenTarget({
    volunteer_id: row.volunteer_id, key: `${visit.visit_id}:${item.questionnaire_id}:0:${Date.now()}`,
  });

  if (loading) return <div className="py-10 text-center text-sm text-stone-400">…</div>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="monitoring-matrix-panel">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard icon={SealCheck} tone="text-teal-700" testId="mx-sum-monitored" label={es ? 'Monitorizados' : 'Monitored'}
          value={pct(s.monitored, s.completed)} sub={`${s.monitored} / ${s.completed} ${es ? 'terminados' : 'finished'}`} />
        <SummaryCard icon={Hourglass} tone="text-green-700" testId="mx-sum-pending" label={es ? 'Pendientes de monitorizar' : 'Pending monitoring'}
          value={s.completed - s.monitored} sub={es ? 'terminados sin revisar' : 'finished, not reviewed'} />
        <SummaryCard icon={ChatCircleText} tone="text-red-700" testId="mx-sum-queries" label={es ? 'Queries abiertas' : 'Open queries'}
          value={s.open_queries} sub={`${s.answered_queries} ${es ? 'respondidas, esperan al monitor' : 'answered, awaiting monitor'}`} />
        <SummaryCard icon={WarningCircle} tone="text-amber-700" testId="mx-sum-late" label={es ? 'Sin hacer (retrasos)' : 'Not done (late)'}
          value={s.late} sub={`${s.not_started} ${es ? 'sin empezar en total' : 'not started in total'}`} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(f => (
          <button key={f.key} type="button" data-testid={`mx-filter-${f.key}`} aria-pressed={!!filters[f.key]}
            onClick={() => setFilters(p => ({ ...p, [f.key]: !p[f.key] }))}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filters[f.key] ? 'border-[#1f3e80] bg-[#1f3e80] text-white' : 'border-stone-300 bg-white text-stone-600 hover:bg-stone-50'}`}>
            {es ? f.es : f.en}
          </button>
        ))}
        <button type="button" data-testid="mx-filter-grey" aria-pressed={includeGrey} onClick={() => setIncludeGrey(v => !v)}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${includeGrey ? 'border-stone-600 bg-stone-600 text-white' : 'border-stone-300 bg-white text-stone-600 hover:bg-stone-50'}`}>
          {es ? 'Incluir excluidos y abandonos' : 'Include excluded and withdrawn'}
        </button>
        <div className="relative ml-auto w-48">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="mx-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={es ? 'Buscar V-code…' : 'Search V-code…'} className="h-8 pl-8 text-xs" />
        </div>
        <Button size="sm" variant="outline" data-testid="mx-refresh" onClick={load} className="h-8 gap-1 text-xs"><ArrowClockwise size={13} />{es ? 'Actualizar' : 'Refresh'}</Button>
      </div>

      <Legend lang={lang} />

      {rows.length === 0 || data.visits.length === 0 ? (
        <p className="rounded-sm border border-stone-200 bg-white py-10 text-center text-sm text-stone-400" data-testid="mx-empty">
          {es ? 'No hay voluntarios incluidos o visitas que mostrar.' : 'No included volunteers or visits to show.'}
        </p>
      ) : (
        <div className="max-h-[70vh] max-w-full w-fit overflow-auto rounded-sm border border-stone-200 bg-white" data-testid="mx-table-wrap" onMouseLeave={() => setHover({ row: null, col: null })}>
          <table className="border-separate border-spacing-0 text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 border-b border-r border-stone-200 bg-stone-50 px-3 py-2 text-left font-semibold text-stone-600">{es ? 'Voluntario' : 'Volunteer'}</th>
                {data.visits.map(v => (
                  <th key={v.visit_id} title={v.name} data-testid={`mx-col-${v.code}`}
                    className={`sticky top-0 z-20 min-w-[64px] border-b border-stone-200 px-2 py-2 text-center font-mono font-semibold ${hover.col === v.visit_id ? 'bg-[#1f3e80]/10 text-[#1f3e80]' : 'bg-stone-50 text-stone-600'}`}>
                    {v.code}
                  </th>
                ))}
                <th className="sticky top-0 z-20 border-b border-l border-stone-200 bg-stone-50 px-3 py-2 text-center font-semibold text-stone-500">% {es ? 'monit.' : 'mon.'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.volunteer_id} data-testid={`mx-row-${r.code || r.volunteer_id}`} className={r.grey ? 'opacity-50' : ''}>
                  <td title={r.name} className={`sticky left-0 z-10 border-b border-r border-stone-100 px-3 py-1.5 font-mono font-semibold ${hover.row === r.volunteer_id ? 'bg-[#1f3e80]/10 text-[#1f3e80]' : 'bg-white text-stone-700'}`}>
                    {r.code || r.volunteer_id}
                  </td>
                  {data.visits.map(v => {
                    const cell = r.cells[v.visit_id] || { state: 'na' };
                    const dimmed = activeFilters.length > 0 && !activeFilters.some(f => cellMatches(cell, f));
                    const hl = hover.row === r.volunteer_id || hover.col === v.visit_id;
                    return (
                      <td key={v.visit_id} className={`border-b border-stone-100 px-2 py-1.5 ${hl ? 'bg-[#1f3e80]/[0.04]' : ''}`}>
                        <MatrixCell cell={cell} row={r} visit={v} dimmed={dimmed} lang={lang} onOpen={openQuestionnaire}
                          onHover={() => setHover({ row: r.volunteer_id, col: v.visit_id })} />
                      </td>
                    );
                  })}
                  <td className="border-b border-l border-stone-100 px-3 py-1.5 text-center text-[11px] text-stone-500" data-testid={`mx-row-pct-${r.code || r.volunteer_id}`}>{pct(r.monitored, r.completed)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="sticky bottom-0 left-0 z-30 border-r border-t border-stone-200 bg-stone-50 px-3 py-2 text-[11px] font-semibold text-stone-500">% {es ? 'monit.' : 'mon.'}</td>
                {data.visits.map(v => (
                  <td key={v.visit_id} data-testid={`mx-col-pct-${v.code}`} className="sticky bottom-0 z-20 border-t border-stone-200 bg-stone-50 px-2 py-2 text-center text-[11px] text-stone-500">{pct(v.monitored, v.completed)}</td>
                ))}
                <td className="sticky bottom-0 z-20 border-l border-t border-stone-200 bg-stone-50 px-3 py-2 text-center text-[11px] font-semibold text-stone-600">{pct(s.monitored, s.completed)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {openTarget && (
        <VolunteersPanel studyId={studyId} lang={lang} shellHidden onFillClosed={load}
          autoExpandVolunteerId={openTarget.volunteer_id} openQuestionnaireKey={openTarget.key} />
      )}
    </div>
  );
}
